import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  ListSubheader,
  FormControl
} from '@mui/material';
import Multiselect from 'multiselect-react-dropdown';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import { updateTeamResources } from 'src/services/projectService';
import { AppActions } from 'src/store/AppReducer';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import moment from 'moment';
import { useAppSelector } from 'src/hooks/hooks';
import { DatePicker } from '@mui/lab';
import 'src/content/projectDetails/ProjectDetails.css';
import { getCalendarMonthInRange } from './calendarMonth';

const UpdateForm = ({
  open,
  toggle,
  getData,
  data,
  billingRoles,
  tracks,
  allSkills,
  allEmployees,
  projectCode,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage,
  projectDetails
}) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 5,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px',
    width: '100%'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const selectStyle = {
    width: '90%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const [teamUpdateObj, setTeamUpdateObj] = useState<any>({});

  const [allocateResource, setAllocateResource] = useState<any>({});
  const [createRoleObjErrors, setCreateRoleObjErrors] = useState<any>({});
  const [previousResourceEndDate, setPreviousResourceEndDate] = useState(''); 

  const [isResourceAllocations, setIsResourceAllocations] = useState(false);
  const [isCancel, setIsCancel] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const [selectedResourceInfo, setSelectedResourceInfo] = useState<any>({});

  useEffect(() => {
    const selectedIndex = data?.selectedResourceIndex ?? 0;
    const selectedResource = data?.resourceAllocations?.[selectedIndex];
    const previousResource = data?.resourceAllocations?.[selectedIndex + 1];

    setTeamUpdateObj({
      projectCode,
      code: data.code,
      role: data.role,
      roleId: data.roleId,
      roleType: data.type,
      roleTrackCode: data.trackCode,
      roleStartDate: data.startDate,
      roleEndDate: data.endDate,
      roleAllocation: data.allocation,
      skills: data.skills
    });
    if (selectedResource) {
      setPreviousResourceEndDate(previousResource?.endDate);
      setIsResourceAllocations(true);
      setAllocateResource({
        employeeId: selectedResource.employeeId,
        resourceName: selectedResource.name,
        resourceType: selectedResource.type,
        resourceAllocation: selectedResource.allocation,
        allocationStartDate: selectedResource.startDate,
        allocationEndDate: selectedResource.endDate,
        allocationStatus: selectedResource.status
      });
    } else {
      setIsResourceAllocations(false);
    }
    setIsCancel(false);
  }, [data, projectCode, isCancel]);

  const onSelect = (selectedList, selectedItem) => {
    setTeamUpdateObj((prev) => ({
      ...prev,
      skills: selectedList.map((sel) => sel.name)
    }));
  };

  const onRemove = (selectedList, selectedItem) => {
    setTeamUpdateObj((prev) => ({
      ...prev,
      skills: selectedList.map((sel) => sel.name)
    }));
  };

  const [openConfirmation, setOpenConfirmation] = useState(false);

  const isRoleStartAfterResourceStart = () => {
    const selectedIndex = data?.selectedResourceIndex ?? 0;
    const allocations = data?.resourceAllocations || [];
    const roleStart = moment(teamUpdateObj.roleStartDate).startOf('day');
    const resourceStarts = allocations.length
      ? allocations.map((resource, index) =>
          index === selectedIndex
            ? allocateResource.allocationStartDate
            : resource?.startDate
        )
      : [allocateResource.allocationStartDate];

    return resourceStarts.some((startDate) => {
      if (!startDate) {
        return false;
      }
      return roleStart.isAfter(moment(startDate).startOf('day'));
    });
  };

  const toggleConfirmation = () => {
    if (openConfirmation) {
      setOpenConfirmation(false);
    } else {
      const errorsObj: any = {};
      if (!teamUpdateObj.role) {
        errorsObj.roleErrors = 'Project role is required.';
      } else if (!teamUpdateObj.roleStartDate) {
        errorsObj.startDateError = 'Start date is required.';
      } else if (!teamUpdateObj.roleEndDate) {
        errorsObj.endDateError = 'End date is required.';
      } else if (
        Date.parse(teamUpdateObj.roleStartDate) >
        Date.parse(teamUpdateObj.roleEndDate)
      ) {
        errorsObj.endDateError = 'End date should be greater than start date';
      } else if (isRoleStartAfterResourceStart()) {
        errorsObj.startDateError =
          "Role start date cannot be later than the allocated resource's start date.";
      } else if (!teamUpdateObj.roleAllocation) {
        errorsObj.allocationError = 'Allocation is required.';
      } else if (!teamUpdateObj.roleType) {
        errorsObj.typeError = 'Role Type is required.';
      } else if (!teamUpdateObj.roleTrackCode) {
        errorsObj.trackCodeError = 'Track Code is required.';
      } else if (!teamUpdateObj.skills) {
        errorsObj.skillError = 'Skill is required.';
      } else if (
        allocateResource.resourceName &&
        !allocateResource.resourceAllocation
      ) {
        errorsObj.empAllocationError = 'Resource allocation is required.';
      } else if(teamUpdateObj.roleAllocation < allocateResource.resourceAllocation) {
        errorsObj.empAllocationError = 'Resource Allocation should be equal or less than to the Role Allocation percentage.';
      } else if (
        allocateResource.resourceName &&
        !allocateResource.allocationStartDate
      ) {
        errorsObj.empStartDateError = 'Resource start date is required.';
      } else if (
        allocateResource.resourceName &&
        !allocateResource.allocationEndDate
      ) {
        errorsObj.empEndDateError = 'Resource end date is required.';
      } else if (
        Date.parse(allocateResource.allocationStartDate) >
        Date.parse(allocateResource.allocationEndDate)
      ) {
        errorsObj.empEndDateError =
          'End date should be greater than start date';
      }
      setCreateRoleObjErrors(errorsObj);
      if (Object.keys(errorsObj).length === 0) {
        setOpenConfirmation(!openConfirmation);
      }
    }
  };

  const onSubmit = () => {
    dispatch(AppActions.showLoader());
    const selectedIndex = data?.selectedResourceIndex ?? 0;
    const selectedResource = data?.resourceAllocations?.[selectedIndex];
    const body = {
      allocationId: selectedResource?.id ? selectedResource.id : null,
      ...teamUpdateObj,
      ...allocateResource
    };
    updateTeamResources(body)
      .then(() => {
        setSnackBarMessage('You’ve updated Project Role Successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        setSearchText('');
        setSearchText2('');
        getData();
        toggle();
        toggleConfirmation();
        setTeamUpdateObj({});
        setAllocateResource({});
        setResetKey((prevKey) => prevKey + 1);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        setSnackBarMessage(error?.response?.data ? error?.response?.data : 'Unable to update the Project Role.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        dispatch(AppActions.hideLoader());
      });
  };

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [selectedOption, setSelectedOption] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSelectedOption(allocateResource.employeeId);
  }, [allocateResource]);

  const displayedOptions = useMemo(
    () =>
      allEmployees.length > 0 &&
      allEmployees?.filter((option) => containsText(option.name, searchText) && !option?.employeeFinance?.isResign),
    [searchText, allEmployees]
  );

  const [selectedOption2, setSelectedOption2] = useState('');
  const [searchText2, setSearchText2] = useState('');
  useEffect(() => {
    setSelectedOption2(teamUpdateObj.roleId);
  }, [teamUpdateObj]);

  const displayedOptions2 = useMemo(
    () =>
      billingRoles.length > 0 &&
      billingRoles?.filter((option) => containsText(option.name, searchText2)),
    [searchText2, billingRoles]
  );

  //set track code
  useEffect(() => {
    billingRoles?.filter((item) => {
      if (item.id === teamUpdateObj.roleId) {
        if (
          item.name === 'Senior Project Manager' ||
          item.name === 'Project Manager' ||
          item.name === 'Associate Project Manager' ||
          item.name === 'Intern Project Manager' ||
          item.name === 'Project Coordinator' ||
          item.name === 'Team Lead' ||
          item.name === 'Associate Team Lead' ||
          item.name === 'Senior Team Lead' ||
          item.name === 'Senior Team Lead/Scrum Master' ||
          item.name === 'Technical Project Manager' ||
          item.name === 'Head Of Delivery'
        ) {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'PM'
          }));
        } else if (
          item.name === 'Quality Assurance Manager' ||
          item.name === 'Quality Assurance Lead' ||
          item.name === 'Senior QA Engineer' ||
          item.name === 'QA Engineer' ||
          item.name === 'Associate QA Engineer' ||
          item.name === 'Intern QA Engineer' ||
          item.name === 'Associate QA Lead'
        ) {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'QA'
          }));
        } else if (
          item.name === 'Lead Business Analyst ' ||
          item.name === 'Associate Lead Business Analyst' ||
          item.name === 'Senior Business Analyst' ||
          item.name === 'Business Analyst' ||
          item.name === 'Associate Business Analyst' ||
          item.name === 'Intern Business Analyst' ||
          item.name === 'Business Consulatant'
        ) {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'BA'
          }));
        } else if (
          item.name === 'Management Trainee' ||
          item.name === 'Intern Finance & Administration' ||
          item.name === 'Manager Finance & Administration' ||
          item.name === 'Administrative Assistant' ||
          item.name === 'Assistant Accountant' ||
          item.name === 'System Adminstrator' ||
          item.name === 'Trainee Accountant' ||
          item.name === 'Assistant System Administrator'
        ) {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'Admin'
          }));
        } else if (
          item.name === 'Senior HR Executive' ||
          item.name === 'HR Executive' ||
          item.name === 'Manager Operations' ||
          item.name === 'Manager HR' ||
          item.name === 'Assistant Manager HR' ||
          item.name === 'Chief Operating Officer' ||
          item.name === 'Managing Director' ||
          item.name === 'Chief Executive Officer' ||
          item.name === 'Manager Business Development'
        ) {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'Ops'
          }));
        } else if (item.name === 'Not Applicable') {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'NA'
          }));
        } else {
          setTeamUpdateObj((prev) => ({
            ...prev,
            roleTrackCode: 'Dev'
          }));
        }
      }
    });
  }, [teamUpdateObj.roleId]);

  const onClose = () => {
    toggle();
    setTeamUpdateObj({});
    setSearchText('');
    setSearchText2('');
    setAllocateResource({});
    setCreateRoleObjErrors({});
    setIsCancel(true);
    setResetKey((prevKey) => prevKey + 1);
  }

  return (
    <Grid sx={{ p: 2 }}>
      <Typography sx={{ mb: 2 }} variant="h4" component="h4">
        {data.role}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Project Role<span style={{ color: 'red' }}>*</span>
          </Typography>
          {/* <Select
            size="small"
            displayEmpty
            defaultValue={teamUpdateObj.roleId}
            inputProps={{ 'aria-label': 'Without label' }}
            style={selectStyle}
            value={teamUpdateObj.roleId || ''}
            onChange={(e) => {
              const filteredRole = billingRoles.filter(
                (fil) => e.target.value === fil.id
              )[0];
              setTeamUpdateObj((prev) => ({
                ...prev,
                role: filteredRole?.name
              }));
              setTeamUpdateObj((prev) => ({
                ...prev,
                roleId: e.target.value
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                roleErrors: ''
              }));
            }}
          >
            {(billingRoles || []).map((item, i) => {
              return (
                <MenuItem value={item.id} key={i}>
                  {item.name}
                </MenuItem>
              );
            })}
          </Select> */}
          <Select
            MenuProps={{ autoFocus: false }}
            labelId="search-select-label"
            style={{
              height: '2.25rem',
              width: '94%',
              marginLeft: 15,
              marginTop: 5,
              paddingRight: 15,
              fontSize: '13px'
            }}
            id="search-select"
            displayEmpty
            value={selectedOption2 || ''}
            onChange={(e) => {
              setSelectedOption2(e.target.value);
              const filteredRole = billingRoles.filter(
                (fil) => e.target.value === fil.id
              )[0];
              setTeamUpdateObj((prev) => ({
                ...prev,
                role: filteredRole?.name
              }));
              setTeamUpdateObj((prev) => ({
                ...prev,
                roleId: e.target.value
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                roleErrors: ''
              }));
            }}
            onClose={() => setSearchText2('')}
          >
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Type to search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchText2(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
            <MenuItem style={{ display: 'none' }} selected disabled value={''}>
              Select Role
            </MenuItem>
            {displayedOptions2.length > 0 &&
              displayedOptions2?.map((option, i) => (
                <MenuItem key={i} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
          </Select>
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {createRoleObjErrors.roleErrors}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Start Date<span style={{ color: 'red' }}>*</span>
          </Typography>
          <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
            <FormControl fullWidth>
              <DatePicker
                InputProps={{
                  className: 'date-picker'
                }}
                renderInput={(params) => (
                  <TextField {...params} id="allocateRoleStartDate" />
                )}
                key={teamUpdateObj?.roleStartDate}
                value={
                  teamUpdateObj?.roleStartDate
                    ? teamUpdateObj.roleStartDate
                    : ''
                }
                minDate={projectDetails?.type !== "Pre-sale" ? 
                  ((projectDetails?.actualStartDate !== null && projectDetails?.actualStartDate !== '0001-01-01T00:00:00')) ? new Date(projectDetails?.actualStartDate) :
                  ((projectDetails?.actualStartDate === null || projectDetails?.actualStartDate === '0001-01-01T00:00:00') && 
                  (projectDetails?.plannedStartDate !== null && projectDetails?.plannedStartDate !== '0001-01-01T00:00:00')) && new Date(projectDetails?.plannedStartDate)
                  : null
                }
                maxDate={projectDetails?.type !== "Pre-sale" ? (projectDetails?.actualEndDate !== null && projectDetails?.actualEndDate !== '0001-01-01T00:00:00') ? new Date(projectDetails?.actualEndDate) :
                  ((projectDetails?.actualEndDate === null || projectDetails?.actualEndDate === '0001-01-01T00:00:00')) && null
                   : null
                }
                onChange={(newValue) => {
                  if (newValue) {
                    setTeamUpdateObj((prev) => ({
                      ...prev,
                      roleStartDate: moment(newValue).format('YYYY-MM-DD')
                    }));
                    setCreateRoleObjErrors((prev) => ({
                      ...prev,
                      startDateError: ''
                    }));
                  }
                }}
              />
            </FormControl>
            <Typography
              style={{
                fontSize: '11px',
                textAlign: 'left',
                marginTop: 4
              }}
              color="red"
            >
              {createRoleObjErrors.startDateError}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            End Date<span style={{ color: 'red' }}>*</span>
          </Typography>
          <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
            <FormControl fullWidth>
              <DatePicker
                InputProps={{
                  className: 'date-picker'
                }}
                minDate={new Date(teamUpdateObj.roleStartDate)}
                maxDate={projectDetails?.type !== "Pre-sale" ? (projectDetails?.actualEndDate !== null && projectDetails?.actualEndDate !== '0001-01-01T00:00:00') ? new Date(projectDetails?.actualEndDate) :
                  ((projectDetails?.actualEndDate === null || projectDetails?.actualEndDate === '0001-01-01T00:00:00')) && null : 
                  null
                }
                renderInput={(params) => (
                  <TextField {...params} id="allocateRoleEndDate" />
                )}
                key={teamUpdateObj?.roleEndDate}
                value={
                  teamUpdateObj?.roleEndDate ? teamUpdateObj.roleEndDate : ''
                }
                onChange={(newValue) => {
                  if (newValue) {
                    setTeamUpdateObj((prev) => ({
                      ...prev,
                      roleEndDate: moment(newValue).format('YYYY-MM-DD')
                    }));
                    setCreateRoleObjErrors((prev) => ({
                      ...prev,
                      endDateError: ''
                    }));
                  }
                }}
              />
            </FormControl>
          </div>
          <Typography
            style={{
              fontSize: '9px',
              textTransform: 'capitalize'
            }}
            color="red"
          >
            {teamUpdateObj.endDateError}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Allocation<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            className="input-text"
            size="small"
            variant="outlined"
            style={TextFieldStyle}
            inputProps={{
              max: 100,
              min: 0,
              style: {
                fontSize: '13px'
              }
            }}
            type="number"
            value={teamUpdateObj.roleAllocation}
            onChange={(e) => {
              let value = parseInt(e.target.value, 10);
              if (value > 100) value = 100;

              setTeamUpdateObj((prev) => ({
                ...prev,
                roleAllocation: value
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                allocationError: ''
              }));
            }}
          />
          <Typography
            style={{
              fontSize: '9px',
              textTransform: 'capitalize'
            }}
            color="red"
          >
            {teamUpdateObj.allocationError}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Role Type<span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            size="small"
            id="designation"
            displayEmpty
            defaultValue={teamUpdateObj.roleType}
            inputProps={{ 'aria-label': 'Without label' }}
            style={selectStyle}
            value={teamUpdateObj.roleType || ''}
            onChange={(e) => {
              setTeamUpdateObj((prev) => ({
                ...prev,
                roleType: e.target.value
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                typeError: ''
              }));
            }}
          >
            <MenuItem value={`Planned`}>Planned</MenuItem>
            <MenuItem value={`Unplanned`}>Unplanned</MenuItem>
          </Select>
          <Typography
            style={{
              fontSize: '9px',
              textTransform: 'capitalize'
            }}
            color="red"
          >
            {teamUpdateObj.typeError}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Track Code<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
              id="roleTrackCode"
              size="small"
              disabled
              inputProps={{
                style: {
                  fontSize: '13px'
                }
              }}
              placeholder="Track Code"
              style={TextFieldStyle}
              value={teamUpdateObj.roleTrackCode || ''}
              onChange={(e) => {}}
            ></TextField>
          <Typography
            style={{
              fontSize: '9px',
              textTransform: 'capitalize'
            }}
            color="red"
          >
            {teamUpdateObj.trackCodeError}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Skills
          </Typography>
          <Multiselect
            id="allSkills"
            style={{
              multiselectContainer: {
                paddingLeft: 15,
                paddingRight: 15,
                width: '33.3%'
              }
            }}
            key={resetKey}
            options={allSkills}
            onSelect={onSelect}
            onRemove={onRemove}
            displayValue="name" // Property name to display in the dropdown options
            selectedValues={teamUpdateObj?.skills?.map((skill) => {
              return { name: skill };
            })}
          />
          <Typography
            style={{
              fontSize: '9px',
              textTransform: 'capitalize'
            }}
            color="red"
          >
            {teamUpdateObj.allocationError}
          </Typography>
        </Grid>
        {isResourceAllocations && (<>
        <Grid item xs={4} sx={{ mt: 2 }}>
          <Typography
            style={LabelStyle}
            variant="h4"
            component="h4"
            fontWeight="light"
          >
            Resource Name
          </Typography>
          <Select
            id="selectedOption"
            MenuProps={{ autoFocus: false }}
            labelId="search-select-label"
            style={{
              height: '2.25rem',
              width: '94%',
              marginLeft: 15,
              marginTop: 5,
              paddingRight: 15,
              fontSize: '13px'
            }}
            displayEmpty
            value={selectedOption || ''}
            onChange={(e) => {
              setSelectedOption(e.target.value);
              const id = e.target.value;
              const filteredEmp = allEmployees.filter(
                (emp) => emp.employeeId === id
              )[0];
              setSelectedResourceInfo(filteredEmp);

              setAllocateResource((prev) => ({
                ...prev,
                employeeId: e.target.value
              }));
              setAllocateResource((prev) => ({
                ...prev,
                resourceName: filteredEmp.name
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                empError: ''
              }));
            }}
            onClose={() => setSearchText('')}
          >
            <ListSubheader>
              <TextField
                id="search-selectedOption"
                size="small"
                autoFocus
                placeholder="Type to search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
            <MenuItem style={{ display: 'none' }} selected disabled value={''}>
              Select Resource
            </MenuItem>
            {displayedOptions.length > 0 &&
              displayedOptions?.map((option, i) => (
                <MenuItem key={i} value={option.employeeId}>
                  {option.name}
                </MenuItem>
              ))}
          </Select>
          {createRoleObjErrors.empError && (
            <Typography
              style={{
                fontSize: '9px',
                textTransform: 'capitalize'
              }}
              color="red"
            >
              {createRoleObjErrors.empError}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} sx={{ mt: 2 }}>
          <Typography
            style={LabelStyle}
            variant="h4"
            component="h4"
            fontWeight="light"
          >
            Resource Type
          </Typography>
          <Select
            id="allocateResourceResourceName"
            disabled={!allocateResource.resourceName}
            size="small"
            displayEmpty
            defaultValue={allocateResource.resourceType}
            inputProps={{ 'aria-label': 'Without label' }}
            style={selectStyle}
            value={allocateResource.resourceType || ''}
            onChange={(e) => {
              setAllocateResource((prev) => ({
                ...prev,
                resourceType: e.target.value
              }));
              setCreateRoleObjErrors((prev) => ({
                ...prev,
                empTypeError: ''
              }));
            }}
          >
            <MenuItem style={{ display: 'none' }} selected disabled value="">
              Select Resource Type
            </MenuItem>
            <MenuItem value="Billable">Billable</MenuItem>
            <MenuItem value="Non-Billable">Non-Billable</MenuItem>
            <MenuItem value="Shadow">Shadow</MenuItem>
            <MenuItem value="Support">Support</MenuItem>
            <MenuItem value="T&M">T&M</MenuItem>
          </Select>
          {createRoleObjErrors.empTypeError && (
            <Typography
              style={{
                fontSize: '9px',
                textTransform: 'capitalize'
              }}
              color="red"
            >
              {createRoleObjErrors.empTypeError}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} sx={{ mt: 2 }}>
          <Typography
            style={LabelStyle}
            variant="h4"
            component="h4"
            fontWeight="light"
          >
            Resource Allocation
          </Typography>
          <TextField
            id="allocateResourceResourceAllocation"
            disabled={!allocateResource.resourceName}
            size="small"
            variant="outlined"
            style={TextFieldStyle}
            inputProps={{
              max: 100,
              min: 0,
              style: {
                fontSize: '13px'
              }
            }}
            placeholder="Enter Allocation"
            type="number"
            value={allocateResource.resourceAllocation || ''}
            onChange={(e) => {
              let value = parseInt(e.target.value, 10);
              if (value > 100) value = 100;
                  if (value > teamUpdateObj.roleAllocation) {
                    setCreateRoleObjErrors((prev) => ({
                      ...prev,
                      empAllocationError: 'Resource Allocation should be equal or less than to the Role Allocation percentage.'
                    }));
                    setAllocateResource((prev) => ({
                      ...prev,
                      resourceAllocation: value
                    }));
                  } else {
                    setAllocateResource((prev) => ({
                      ...prev,
                      resourceAllocation: value
                    }));
                    setCreateRoleObjErrors((prev) => ({
                      ...prev,
                      empAllocationError: ''
                    }));
                  }
                }}
              />
              {createRoleObjErrors.empAllocationError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize',
                    marginLeft: 15
                  }}
                  color="red"
                >
                  {createRoleObjErrors.empAllocationError}
                </Typography>
              )}
            </Grid>
        <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={LabelStyle}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                Start Date
              </Typography>
              <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
                <FormControl fullWidth>
                  <DatePicker
                    disabled={!allocateResource.resourceName}
                    InputProps={{
                      className: 'date-picker'
                    }}
                    defaultCalendarMonth={getCalendarMonthInRange(
                      teamUpdateObj?.roleStartDate,
                      teamUpdateObj?.roleEndDate
                    )}
                    minDate={new Date(teamUpdateObj.roleStartDate) ? new Date(teamUpdateObj.roleStartDate) :
                      new Date(teamUpdateObj.roleStartDate) > new Date(selectedResourceInfo?.joinedDate) ? 
                      new Date(teamUpdateObj.roleStartDate) : new Date(selectedResourceInfo?.joinedDate)
                    }
                    maxDate={new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                      new Date(teamUpdateObj.roleEndDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                      new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(teamUpdateObj.roleEndDate) : 
                      new Date(teamUpdateObj.roleEndDate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="allocateResourceResourceStartDate"
                      />
                    )}
                    key={allocateResource?.allocationStartDate}
                    value={
                      allocateResource?.allocationStartDate
                        ? allocateResource.allocationStartDate
                        : ''
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        setAllocateResource((prev) => ({
                          ...prev,
                          allocationStartDate:
                            moment(newValue).format('YYYY-MM-DD')
                        }));
                        setCreateRoleObjErrors((prev) => ({
                          ...prev,
                          empStartDateError: ''
                        }));
                      }
                    }}
                  />
                </FormControl>
              </div>
              {createRoleObjErrors.empStartDateError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {createRoleObjErrors.empStartDateError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={LabelStyle}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                End Date
              </Typography>
              <div style={{ marginTop: 5, marginLeft: 10, marginRight: 10 }}>
                <FormControl fullWidth>
                  <DatePicker
                    disabled={!allocateResource.resourceName}
                    InputProps={{
                      className: 'date-picker'
                    }}
                    minDate={new Date(allocateResource.allocationStartDate) ? new Date(allocateResource.allocationStartDate) : 
                      new Date(teamUpdateObj.roleEndDate) > new Date(selectedResourceInfo?.joinedDate) ? 
                      new Date(teamUpdateObj.roleEndDate) : new Date(selectedResourceInfo?.joinedDate)}
                    maxDate={selectedResourceInfo?.employeeFinance?.lastWorkingDate ? 
                      new Date(teamUpdateObj.roleEndDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                      new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(teamUpdateObj.roleEndDate)
                      : new Date(teamUpdateObj.roleEndDate)
                    }
                    renderInput={(params) => (
                      <TextField {...params} id="allocateResourceResourceEndDate" />
                    )}
                    key={allocateResource?.allocationEndDate}
                    value={
                      allocateResource?.allocationEndDate
                        ? allocateResource.allocationEndDate
                        : ''
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        setAllocateResource((prev) => ({
                          ...prev,
                          allocationEndDate: moment(newValue).format('YYYY-MM-DD')
                        }));
                        setCreateRoleObjErrors((prev) => ({
                          ...prev,
                          empEndDateError: ''
                        }));
                      }
                    }}
                  />
                </FormControl>
              </div>
              {createRoleObjErrors.empEndDateError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {createRoleObjErrors.empEndDateError}
                </Typography>
              )}
            </Grid>
          </>)}
      </Grid>
      <Grid
        item
        sx={{
          mt: 3,
          pr: 2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
        xs={12}
      >
        <Button
          id="btn-update-toggle"
          sx={{
            width: '10%',
            fontWeight: 'normal',
            background: '#F5F7FF',
            fontSize: '12px'
          }}
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          id="btn-update-toggleConfirmation"
          onClick={() => toggleConfirmation()}
          disabled={!teamUpdateObj.roleAllocation}
          sx={{
            ml: 2,
            width: '10%',
            fontSize: '12px',
            fontWeight: 'normal',
            background: '#0069E4'
          }}
          variant="contained"
          type="button"
        >
          Update
        </Button>
      </Grid>
      <ConfirmationModal
        isLoading={isLoading}
        open={openConfirmation}
        toggle={toggleConfirmation}
        message={`Are you sure want to update this Project Role?`}
        onConfirm={() => onSubmit()}
        noToggle={toggleConfirmation}
      />
    </Grid>
  );
};

export default UpdateForm;
